import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemTitle,
} from '@/components/ui/item';
import { cn } from '@/lib/utils';
import { ORGANIZATION } from 'components/IAP/ICSConstants';
import ActionDrawer from 'components/IAP/ActionDrawer';
import ResourceSetEditDrawer from 'components/IAP/ResourceSetEditDrawer';
import {
  useOrgActions,
  type GetOrgActionResponse,
} from 'components/IAP/hooks/orgActionHooks';
import {
  useResourceSets,
  type ResourceSet,
} from 'components/IAP/hooks/resourceSetHooks';
import { type ResourceRow } from 'components/MapPage/CorporateViewTabs/ResourceTab/ResourceTabCard';
import { CfaHeleneAssetItem } from './CfaHeleneAssetItem';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import { useOptionalUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import type { ArcgisMapView } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/arcgisView';
import {
  symbolToPictureMarkerMap,
  type UscgSymbol,
} from 'components/MapPage/CorporateViewTabs/LayersTab/utils/uscgSymbology';
import {
  useGetRostersForGroup,
  type GroupRosterItem,
} from 'hooks/incident/useRoster';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';
import {
  getTennesseeMemphisRegionalThreats,
  isTnngMemphisOrgActionName,
  isTnngMemphisResourceSet,
  TNNG_MEMPHIS_ASSETS_LAYER_ID,
  TNNG_MEMPHIS_FACILITIES,
  TNNG_MEMPHIS_RESPONSE_CHECKLIST_ITEMS,
  TNNG_MEMPHIS_SYMBOLOGY,
  tnngFacilityIdFromResourceNotes,
} from './tennesseeMemphisThreatNotification';

function actionStatusBadgeClass(status: string | null | undefined): string {
  switch (status) {
    case 'Closed':
      return 'tw:border-transparent tw:bg-emerald-400/20 tw:text-emerald-800 tw:dark:text-emerald-200';
    case 'Current/Ongoing':
      return 'tw:border-transparent tw:bg-amber-400/25 tw:text-amber-800 tw:dark:text-amber-200';
    case 'Blocked':
      return 'tw:border-transparent tw:bg-destructive/15 tw:text-destructive';
    case 'Planned':
    default:
      return 'tw:border-transparent tw:bg-blue-500/20 tw:text-blue-100';
  }
}

function tnngResourceSetToRow(
  resourceSet: ResourceSet,
  groupMembers: GroupRosterItem[]
): ResourceRow {
  const coordinates =
    resourceSet.geometry &&
    typeof resourceSet.geometry === 'object' &&
    Array.isArray(resourceSet.geometry.coordinates)
      ? resourceSet.geometry.coordinates
      : undefined;

  const facilityId = tnngFacilityIdFromResourceNotes(resourceSet.notes);
  const facility = facilityId
    ? TNNG_MEMPHIS_FACILITIES.find((entry) => entry.id === facilityId)
    : undefined;

  const latitude =
    coordinates?.[1] != null
      ? Number(coordinates[1])
      : facility != null
        ? facility.lat
        : undefined;
  const longitude =
    coordinates?.[0] != null
      ? Number(coordinates[0])
      : facility != null
        ? facility.lon
        : undefined;

  const pocMember = resourceSet.point_of_contact
    ? groupMembers.find((m) => m.user_guid === resourceSet.point_of_contact)
    : undefined;

  return {
    id: resourceSet.id,
    resource_set_id: resourceSet.resource_set_id,
    name: resourceSet.name,
    kind: resourceSet.kind,
    type: resourceSet.type || '',
    assetStatus: resourceSet.asset_status || '',
    assetStatusTimestamp: resourceSet.asset_status_timestamp || '',
    incident: resourceSet.incident_details?.[0]
      ? {
          id: resourceSet.incident_details[0].id,
          name: resourceSet.incident_details[0].name,
        }
      : null,
    incident_details: resourceSet.incident_details,
    owning_organization: resourceSet.owning_organization || '',
    description: resourceSet.description,
    datetimeOrdered: resourceSet.date_time_ordered,
    datetimeOrderedOffset: resourceSet.datetime_ordered_offset,
    quantity: resourceSet.quantity,
    status: resourceSet.current_status || resourceSet.status,
    inRequest: resourceSet.in_request,
    aor: resourceSet.aor,
    opcon: resourceSet.opcon,
    tacon: resourceSet.tacon,
    persons_on_board: resourceSet.persons_on_board,
    hull_tail_number: resourceSet.hull_tail_number,
    unit: resourceSet.unit,
    address: resourceSet.address,
    symbology: resourceSet.symbology,
    latitude,
    longitude,
    notes: resourceSet.notes,
    eta: resourceSet.eta,
    etaTimezoneOffset: resourceSet.eta_timezone_offset,
    assignees: resourceSet.resource_in_incident_details
      ? resourceSet.resource_in_incident_details.flatMap((detail) =>
          (detail.assignees || []).map((assignee) => ({
            userGuid: assignee.id,
            userName: assignee.name,
            emailAddress: assignee.email,
          }))
        )
      : [],
    pointOfContact: pocMember
      ? {
          userGuid: pocMember.user_guid,
          emailAddress: pocMember.email_address,
          userName: pocMember.user_name,
        }
      : undefined,
  };
}

function tnngPictureMarker(symbology: string | undefined) {
  const key = (symbology || TNNG_MEMPHIS_SYMBOLOGY) as UscgSymbol;
  return symbolToPictureMarkerMap[key] ?? symbolToPictureMarkerMap.facility;
}

function focusTnngFacilityOnMap({
  resource,
  mapView,
  focusFeatureOnMap,
}: {
  resource: ResourceSet;
  mapView: ArcgisMapView | null | undefined;
  focusFeatureOnMap:
    | ((params: { graphic: __esri.Graphic }) => Promise<void>)
    | undefined;
}) {
  if (!focusFeatureOnMap) return;

  const facilityId = tnngFacilityIdFromResourceNotes(resource.notes);
  const facility = facilityId
    ? TNNG_MEMPHIS_FACILITIES.find((entry) => entry.id === facilityId)
    : undefined;

  const layer = mapView?.map?.findLayerById(TNNG_MEMPHIS_ASSETS_LAYER_ID) as
    | GraphicsLayer
    | undefined;
  const liveGraphic = facilityId
    ? layer?.graphics.toArray().find((graphic) => {
        const attrs = graphic.attributes ?? {};
        return String(attrs.id ?? '') === facilityId;
      })
    : undefined;

  if (liveGraphic) {
    void focusFeatureOnMap({ graphic: liveGraphic });
    return;
  }

  const coordinates =
    resource.geometry &&
    typeof resource.geometry === 'object' &&
    Array.isArray(resource.geometry.coordinates)
      ? resource.geometry.coordinates
      : undefined;
  const longitude =
    coordinates?.[0] != null ? Number(coordinates[0]) : facility?.lon;
  const latitude =
    coordinates?.[1] != null ? Number(coordinates[1]) : facility?.lat;

  if (
    longitude == null ||
    latitude == null ||
    Number.isNaN(longitude) ||
    Number.isNaN(latitude)
  ) {
    return;
  }

  const graphic = new Graphic({
    geometry: new Point({
      longitude,
      latitude,
    }),
    symbol: tnngPictureMarker(resource.symbology || facility?.symbology),
    attributes: {
      id: facilityId,
      name: resource.name,
      description: resource.description,
    },
  });
  void focusFeatureOnMap({ graphic });
}

type TnngMemphisImpactSectionsProps = {
  assetItemDensity?: 'default' | 'comfortable';
};

/**
 * TNG-only Assets at Risk + Response Checklist backed by seeded resource sets
 * and org actions. Not used when CFA / EPA / DHS is on.
 */
export function TnngMemphisImpactSections({
  assetItemDensity = 'default',
}: TnngMemphisImpactSectionsProps) {
  const region = getTennesseeMemphisRegionalThreats().region;
  const { data: orgActions = [] } = useOrgActions(ORGANIZATION);
  const { data: resourceSets = [] } = useResourceSets();
  const groupGuid = useAppSelector(selectGroupGuid);
  const { data: groupMembers = [] } = useGetRostersForGroup(groupGuid || '');
  const { focusFeatureOnMap, mapView } = useOptionalUscgMapContext() ?? {};

  const [selectedResourceSetId, setSelectedResourceSetId] = useState<
    string | null
  >(null);
  const [openAssetIds, setOpenAssetIds] = useState<Set<string>>(
    () => new Set()
  );
  const [selectedAction, setSelectedAction] =
    useState<GetOrgActionResponse | null>(null);
  const [selectedResource, setSelectedResource] = useState<ResourceSet | null>(
    null
  );

  const tnngResources = useMemo(() => {
    const facilityOrder = new Map(
      TNNG_MEMPHIS_FACILITIES.map((facility, index) => [facility.id, index])
    );
    return resourceSets
      .filter(isTnngMemphisResourceSet)
      .slice()
      .sort((left, right) => {
        const leftOrder =
          facilityOrder.get(
            tnngFacilityIdFromResourceNotes(left.notes) ?? ''
          ) ?? 999;
        const rightOrder =
          facilityOrder.get(
            tnngFacilityIdFromResourceNotes(right.notes) ?? ''
          ) ?? 999;
        return leftOrder - rightOrder;
      });
  }, [resourceSets]);

  const checklistActions = useMemo(() => {
    const byName = new Map(
      orgActions
        .filter(
          (action) =>
            !action.archived && isTnngMemphisOrgActionName(action.name)
        )
        .map((action) => [action.name, action] as const)
    );
    return TNNG_MEMPHIS_RESPONSE_CHECKLIST_ITEMS.flatMap((item) => {
      const action = byName.get(item.label);
      return action ? [action] : [];
    });
  }, [orgActions]);

  return (
    <div className="tw:min-w-0 tw:space-y-4 tw:text-sm tw:leading-snug">
      <div className="tw:min-w-0">
        <p className="tw:m-0 tw:font-medium tw:text-foreground">
          Assets at Risk in {region}
        </p>
        <div className="tw:mt-2 tw:min-w-0 tw:space-y-1.5">
          {tnngResources.length === 0 ? (
            <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
              Seeding TNNG resources…
            </p>
          ) : (
            tnngResources.map((resource) => (
              <CfaHeleneAssetItem
                key={resource.resource_set_id}
                resource={resource}
                row={tnngResourceSetToRow(resource, groupMembers)}
                density={assetItemDensity}
                includeChecklist={false}
                selected={selectedResourceSetId === resource.resource_set_id}
                open={openAssetIds.has(resource.resource_set_id)}
                onOpenChange={(nextOpen) => {
                  setOpenAssetIds((previous) => {
                    const next = new Set(previous);
                    if (nextOpen) next.add(resource.resource_set_id);
                    else next.delete(resource.resource_set_id);
                    return next;
                  });
                }}
                onMapClick={() => {
                  setSelectedResourceSetId(resource.resource_set_id);
                  setOpenAssetIds((previous) => {
                    const next = new Set(previous);
                    next.add(resource.resource_set_id);
                    return next;
                  });
                  focusTnngFacilityOnMap({
                    resource,
                    mapView,
                    focusFeatureOnMap,
                  });
                }}
                onEditClick={() => setSelectedResource(resource)}
              />
            ))
          )}
        </div>
      </div>

      <div>
        <p className="tw:m-0 tw:font-medium tw:text-foreground">
          Response Checklist
        </p>
        <div className="tw:mt-2 tw:space-y-1.5">
          {checklistActions.length === 0 ? (
            <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
              Seeding TNNG actions…
            </p>
          ) : (
            checklistActions.map((action) => (
              <Item
                key={action.action_id}
                variant="outline"
                size="xs"
                className="tw:cursor-pointer tw:items-start tw:gap-2 tw:px-2.5 tw:py-2 tw:hover:bg-muted/40"
                onClick={() => setSelectedAction(action)}
              >
                <ItemContent className="tw:min-w-0">
                  <ItemTitle className="tw:line-clamp-3 tw:text-sm tw:font-normal tw:text-foreground">
                    {action.name}
                  </ItemTitle>
                  <p className="tw:m-0 tw:mt-0.5 tw:text-[11px] tw:text-muted-foreground">
                    {action.poc_user_name || 'Unassigned'}
                  </p>
                </ItemContent>
                <ItemActions>
                  <Badge
                    className={cn(
                      'tw:shrink-0 tw:text-[10px]',
                      actionStatusBadgeClass(action.status)
                    )}
                  >
                    {action.status || 'Planned'}
                  </Badge>
                </ItemActions>
              </Item>
            ))
          )}
        </div>
      </div>

      {selectedAction ? (
        <ActionDrawer
          scope={ORGANIZATION}
          selectedEntity={selectedAction}
          toggle={() => setSelectedAction(null)}
        />
      ) : null}
      {selectedResource ? (
        <ResourceSetEditDrawer
          selectedEntity={selectedResource}
          toggle={() => setSelectedResource(null)}
        />
      ) : null}
    </div>
  );
}
